import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { FileDown, Printer, Search, Ban, Trash2 } from "lucide-react";
import api, { formatApiError, downloadFromApi } from "../lib/api";
import PageHeader from "../components/PageHeader";
import AdminActionModal from "../components/AdminActionModal";
import AdminDeleteModal from "../components/AdminDeleteModal";
import { useAuth } from "../contexts/AuthContext";
import { fmtDate } from "../lib/format";

const TABS = [
    { key: "order_confirmation", label: "Order Confirmations", phase: null },
    { key: "packing_list", label: "Packing Lists", phase: null },
    { key: "load_tag", label: "Load Tags", phase: null },
];

export default function Documents() {
    const { user } = useAuth();
    const isAdmin = user?.role === "admin";
    const [activeTab, setActiveTab] = useState("order_confirmation");
    const [docs, setDocs] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [search, setSearch] = useState("");
    const [customerFilter, setCustomerFilter] = useState("");
    const [hideVoided, setHideVoided] = useState(false);
    const [voidTarget, setVoidTarget] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    useEffect(() => {
        api.get("/customers", { params: { active: true } }).then((r) => setCustomers(r.data));
    }, []);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = { type: activeTab };
            if (customerFilter) params.customer_id = customerFilter;
            if (hideVoided) params.include_voided = false;
            const { data } = await api.get("/documents", { params });
            setDocs(data);
            setError("");
        } catch (err) {
            setError(formatApiError(err?.response?.data?.detail) || err.message);
        } finally {
            setLoading(false);
        }
    }, [activeTab, customerFilter, hideVoided]);

    useEffect(() => {
        load();
    }, [load]);

    const filteredDocs = useMemo(() => {
        if (!search.trim()) return docs;
        const q = search.trim().toLowerCase();
        return docs.filter((d) =>
            [d.document_number, d.filename, d.related?.order_id]
                .filter(Boolean)
                .some((v) => String(v).toLowerCase().includes(q)),
        );
    }, [docs, search]);

    const downloadDoc = async (doc) => {
        try {
            await downloadFromApi(`/documents/${doc.id}/download`, doc.filename);
        } catch (err) {
            console.error("downloadDoc failed", err);
            setError(formatApiError(err?.response?.data?.detail) || err.message);
        }
    };

    const reprintDoc = async (doc) => {
        try {
            await downloadFromApi(`/documents/${doc.id}/reprint`, doc.filename);
        } catch (err) {
            console.error("reprintDoc failed", err);
            setError(formatApiError(err?.response?.data?.detail) || err.message);
        }
    };

    const activePhase = TABS.find((t) => t.key === activeTab)?.phase;

    return (
        <div data-testid="documents-page">
            <PageHeader
                title="Documents"
                subtitle="Central repository — every generated PDF is permanently archived and downloadable"
            />

            <div className="bg-white border border-gray-200 rounded-sm p-1 mb-4 inline-flex w-full sm:w-auto">
                {TABS.map((t) => (
                    <button
                        key={t.key}
                        onClick={() => setActiveTab(t.key)}
                        className={`flex-1 sm:flex-none px-4 py-2 text-sm font-semibold rounded-sm transition-colors ${
                            activeTab === t.key
                                ? "bg-[var(--odr-red)] text-white"
                                : "text-gray-700 hover:bg-gray-50"
                        }`}
                        data-testid={`doc-tab-${t.key}`}
                    >
                        {t.label}
                    </button>
                ))}
            </div>

            <div className="bg-white border border-gray-200 rounded-sm p-3 mb-4 flex flex-wrap gap-3 items-end">
                <div className="relative flex-1 min-w-[220px]">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search doc # or filename…"
                        className="odr-input w-full rounded-sm border border-gray-300 pl-9 pr-3 text-sm"
                        data-testid="documents-search"
                    />
                </div>
                <select
                    value={customerFilter}
                    onChange={(e) => setCustomerFilter(e.target.value)}
                    className="odr-input rounded-sm border border-gray-300 px-3 text-sm bg-white min-w-[180px]"
                    data-testid="documents-customer-filter"
                >
                    <option value="">All customers</option>
                    {customers.map((c) => (
                        <option key={c.id} value={c.id}>
                            {c.name}
                        </option>
                    ))}
                </select>
                <label className="inline-flex items-center gap-2 text-xs text-gray-700 cursor-pointer select-none">
                    <input
                        type="checkbox"
                        checked={hideVoided}
                        onChange={(e) => setHideVoided(e.target.checked)}
                        data-testid="documents-hide-voided"
                    />
                    Hide voided
                </label>
            </div>

            {error && (
                <div className="text-sm border border-red-200 bg-red-50 text-red-700 rounded-sm px-3 py-2 mb-4">
                    {error}
                </div>
            )}

            {activePhase && (
                <div className="text-sm border border-amber-200 bg-amber-50 text-amber-900 rounded-sm px-3 py-2 mb-4">
                    {TABS.find((t) => t.key === activeTab)?.label} generation arrives in{" "}
                    <span className="font-semibold">{activePhase}</span>. This tab will
                    show every generated document once that phase is live.
                </div>
            )}

            <div className="bg-white border border-gray-200 rounded-sm overflow-x-auto">
                <table className="odr-table" data-testid="documents-table">
                    <thead>
                        <tr>
                            <th>Document #</th>
                            <th>Filename</th>
                            <th>Version</th>
                            <th>Order</th>
                            <th>Generated</th>
                            <th>By</th>
                            <th>Blind</th>
                            <th className="text-right">Size</th>
                            <th className="text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && (
                            <tr>
                                <td colSpan={9} className="text-center text-gray-500 py-6">
                                    Loading…
                                </td>
                            </tr>
                        )}
                        {!loading && filteredDocs.length === 0 && (
                            <tr>
                                <td colSpan={9} className="text-center text-gray-500 py-6">
                                    No documents found.
                                </td>
                            </tr>
                        )}
                        {filteredDocs.map((d) => (
                            <tr
                                key={d.id}
                                data-testid={`docs-row-${d.document_number}`}
                                className={d.voided ? "opacity-40 line-through" : ""}
                            >
                                <td className="odr-mono text-xs font-semibold">
                                    {d.document_number}
                                    {d.voided && (
                                        <span
                                            className="ml-2 odr-badge odr-badge-past"
                                            title={`Voided by ${d.voided_by}: ${d.void_reason}`}
                                        >
                                            VOIDED
                                        </span>
                                    )}
                                </td>
                                <td className="odr-mono text-xs">{d.filename}</td>
                                <td className="odr-mono">v{d.version}</td>
                                <td className="odr-mono text-xs">
                                    {d.related?.order_id ? (
                                        <Link
                                            to={`/orders/${d.related.order_id}`}
                                            className="text-red-700 hover:text-red-900"
                                        >
                                            view
                                        </Link>
                                    ) : (
                                        "—"
                                    )}
                                </td>
                                <td className="text-xs">{fmtDate(d.generated_at)}</td>
                                <td className="text-xs">{d.generated_by}</td>
                                <td>
                                    {d.blind_shipment ? (
                                        <span className="odr-badge odr-badge-past">Yes</span>
                                    ) : (
                                        <span className="text-xs text-gray-400">—</span>
                                    )}
                                </td>
                                <td className="text-right odr-mono text-xs">
                                    {Math.round((d.size_bytes || 0) / 1024).toLocaleString()} KB
                                </td>
                                <td className="text-right">
                                    <div className="inline-flex items-center gap-3 justify-end">
                                        <button
                                            onClick={() => downloadDoc(d)}
                                            className="text-sm text-red-700 hover:text-red-900 font-semibold inline-flex items-center gap-1"
                                            data-testid={`download-${d.id}`}
                                        >
                                            <FileDown className="w-3.5 h-3.5" /> Download
                                        </button>
                                        {!d.voided && (d.type === "load_tag" || d.type === "packing_list") && (
                                            <button
                                                onClick={() => reprintDoc(d)}
                                                title="Reprint — re-downloads this exact PDF. No new LT batch or PLL numbers are generated."
                                                className="text-sm text-gray-700 hover:text-gray-900 font-semibold inline-flex items-center gap-1"
                                                data-testid={`reprint-${d.id}`}
                                            >
                                                <Printer className="w-3.5 h-3.5" /> Reprint
                                            </button>
                                        )}
                                        {isAdmin && !d.voided && (
                                            <button
                                                onClick={() => setVoidTarget(d)}
                                                title="Admin only — void this document (reason required, audited)"
                                                className="text-xs text-red-700 hover:text-red-900 font-semibold inline-flex items-center gap-1"
                                                data-testid={`void-doc-${d.document_number}`}
                                            >
                                                <Ban className="w-3.5 h-3.5" /> Void
                                            </button>
                                        )}
                                        {isAdmin && (
                                            <button
                                                onClick={() => setDeleteTarget(d)}
                                                title="Admin only — hard delete (password + reason, audited)"
                                                className="text-xs text-red-700 hover:text-red-900 font-semibold inline-flex items-center gap-1"
                                                data-testid={`delete-doc-${d.document_number}`}
                                            >
                                                <Trash2 className="w-3.5 h-3.5" /> Delete
                                            </button>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <AdminActionModal
                open={!!voidTarget}
                title={voidTarget ? `Void document ${voidTarget.document_number}` : ""}
                description={
                    voidTarget ? (
                        <span>
                            The PDF will remain in the archive for audit purposes but
                            will be marked <strong>VOIDED</strong> and{" "}
                            <strong>reprint will be blocked</strong>. Generate a new
                            version of the {voidTarget.type?.replace("_", " ")} if a
                            corrected copy is needed.
                        </span>
                    ) : null
                }
                confirmLabel="Void Document"
                onCancel={() => setVoidTarget(null)}
                onConfirm={async (reason) => {
                    await api.post(`/documents/${voidTarget.id}/void`, { reason });
                    setVoidTarget(null);
                    await load();
                }}
                testIdPrefix="void-doc"
            />

            <AdminDeleteModal
                open={!!deleteTarget}
                module="documents"
                recordId={deleteTarget?.id}
                endpoint={deleteTarget ? `/documents/${deleteTarget.id}` : ""}
                label={deleteTarget ? `document ${deleteTarget.document_number}` : ""}
                onClose={() => setDeleteTarget(null)}
                onDeleted={() => load()}
                testIdPrefix="delete-doc"
            />
        </div>
    );
}
